import React from 'react'
import { useNavigate } from 'react-router-dom'
import InitialAvatar from 'shared/components/InitialAvatar/InitialAvatar'
import { ROUTES } from 'shared/config/routes'
import { fetchStudentApplications } from 'api/applications'
import { ReactComponent as FolderIcon } from 'assets/icons/folder.svg'
import { ReactComponent as UploadIcon } from 'assets/icons/upload-cloud.svg'
import { ReactComponent as CityIcon } from 'assets/icons/city.svg'
import { ReactComponent as ClockIcon } from 'assets/icons/clock.svg'
import { ReactComponent as EyeIcon } from 'assets/icons/openedEye.svg'
import { ReactComponent as ExternalIcon } from 'assets/icons/open-external.svg'
import {
	formatFeedbackCardTime,
	formatFeedbackCardTimeShort,
} from '../../utils/feedbackMapping'
import {
	dashboardPriorityBackState,
	getUrgencyMeta,
	navigateToStudentProfileFromPriority,
} from '../../utils/priorityCardUi'
import s from './DocumentCard.module.scss'

const VISIBLE_LIMIT = 3

const DocumentCard = ({ item, onOpen, sourceTab = null }) => {
	const navigate = useNavigate()
	const docs = Array.isArray(item?.newDocuments) ? item.newDocuments : []
	const visibleDocs = docs.slice(0, VISIBLE_LIMIT)
	const remaining = Math.max(0, docs.length - VISIBLE_LIMIT)
	const newCount = Number(item?.newCount) || docs.length
	const timeLabel = formatFeedbackCardTime(item?.lastUpdatedAt) || 'Recently'
	const timeLabelShort =
		formatFeedbackCardTimeShort(item?.lastUpdatedAt) || timeLabel
	const urgency = getUrgencyMeta(item)
	const meta = [item?.universityName, item?.programName]
		.filter(Boolean)
		.join(' · ')

	const appNavOptions = {
		state: dashboardPriorityBackState(sourceTab),
	}

	const handleOpenApplication = async e => {
		e.stopPropagation()
		const appId = item?.applicationId || item?.applicationTargetId
		if (appId) {
			navigate(
				`${ROUTES.APPLICATIONS}/${encodeURIComponent(appId)}`,
				appNavOptions,
			)
			return
		}
		if (!item?.studentId) return
		try {
			const res = await fetchStudentApplications(item.studentId)
			const apps = Array.isArray(res?.applications)
				? res.applications
				: Array.isArray(res?.data?.applications)
					? res.data.applications
					: []
			const firstId = apps[0]?.id || apps[0]?.applicationId || null
			if (firstId) {
				navigate(
					`${ROUTES.APPLICATIONS}/${encodeURIComponent(firstId)}`,
					appNavOptions,
				)
				return
			}
		} catch (error) {
			console.error(error)
		}
		navigateToStudentProfileFromPriority(
			navigate,
			item.studentId,
			sourceTab,
		)
	}

	const handleOpenStudent = e => {
		e.stopPropagation()
		navigateToStudentProfileFromPriority(
			navigate,
			item?.studentId,
			sourceTab,
		)
	}

	const handleReview = e => {
		e.stopPropagation()
		onOpen?.()
	}

	const studentName = item?.studentName || 'Student'

	return (
		<article className={s.card}>
			<div className={s.header}>
				<div className={s.headerLead}>
					<div className={s.avatar}>
						<InitialAvatar
							size={40}
							name={studentName}
							studentId={item?.studentId}
							photoUrl={item?.studentAvatarUrl}
							avatarSource={
								item?.studentAvatarUrl ? 'photo' : undefined
							}
							onClick={handleOpenStudent}
						/>
					</div>
					<div className={s.identity}>
						<button
							type="button"
							className={s.name}
							onClick={handleOpenStudent}
						>
							{studentName}
						</button>
						{meta ? (
							<span className={s.meta}>
								<CityIcon />
								<span>{meta}</span>
							</span>
						) : null}
					</div>
				</div>
				<div className={s.badgeStack}>
					{urgency?.label ? <span className={s.urgent}>{urgency.label}</span> : null}
					{newCount > 0 ? (
						<span className={s.status}>{newCount} new</span>
					) : null}
				</div>
			</div>

			<div className={s.body}>
				<div className={s.metaRow}>
					<span className={s.wellLabel}>
						<UploadIcon />
						<span>Documents</span>
					</span>
					<span className={s.time}>
						<ClockIcon />
						<span className={s.timeFull}>{timeLabel}</span>
						<span className={s.timeShort}>{timeLabelShort}</span>
					</span>
				</div>

				<div className={s.docWell}>
					<ul className={s.list}>
						{visibleDocs.map(doc => (
							<li key={doc.id} className={s.row}>
								<span className={s.icon}>
									<FolderIcon />
								</span>
								<span className={s.title}>{doc.title}</span>
							</li>
						))}
						{remaining > 0 ? (
							<li className={s.more}>
								+ {remaining} document{remaining === 1 ? '' : 's'}
							</li>
						) : null}
					</ul>
				</div>
			</div>

			<div className={s.footer}>
				<button type="button" className={s.openBtn} onClick={handleOpenApplication}>
					<ExternalIcon />
					Open Application
				</button>
				<button type="button" className={s.reviewBtn} onClick={handleReview}>
					<EyeIcon />
					Review Documents
				</button>
			</div>
		</article>
	)
}

export default DocumentCard
